import { type Opportunity } from "@entities";
import HeadsetMicRoundedIcon from "@mui/icons-material/HeadsetMicRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PersonSearchRoundedIcon from "@mui/icons-material/PersonSearchRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import {
  Badge,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { opportunityRepo } from "@repositories";
import { extractNestHttpErrorMessage } from "@shared/exceptions-message";
import type { ActionButton } from "@shared/frontend/hyper-table/inner-components/hyper-table-actions-bar/ActionsBar";
import type { SingleRowActionGetter } from "@shared/frontend/hyper-table/inner-components/hyper-table-menu-actions-column/HyperTableMenuActionsColumn";
import { linko } from "@shared/linko";
import { useQuery } from "@tanstack/react-query";
import { APIProvider } from "@vis.gl/react-google-maps";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type FC, type ReactNode } from "react";
import { useCallCenterShift } from "../../contexts/CallCenterShiftProvider";
import { useAlert } from "../../contexts/AlertProvider";
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LANGUAGE,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_REGION,
} from "../../constants/google-maps-browser";
import { CallCenterCallerTasks } from "./CallCenterCallerTasks";
import { EventCard } from "../event-card/EventCard";
import { CallCenterHelp } from "./CallCenterHelp";
import { CallCenterChat } from "./CallCenterChat";
import { CallCenterTour, type TourFocus, type TourPreparePayload } from "./CallCenterTour";
import { CallCenterTourEventMock } from "./CallCenterTourEventMock";
import { TOUR_OPPORTUNITY, TOUR_SEARCH_ROWS } from "./call-center-tour-fixtures";
import { CALL_CENTER_MIN_TASK_MS } from "./call-center-ui.constants";
import { CC_BRAND } from "./call-center-brand";
import { CC, ccDialogPaperSx, ccFieldSx } from "./call-center-ui.theme";
import { CcButton, CcChip, CcEmpty, CcEyebrow, CcProgress, CcTrackChip } from "./CallCenterUi";

const OpportunitiesTable = lazy(() => import("../tables/opportunities-table"));

function toDatetimeLocal(date: Date) {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const SHIFT_MAX_MS = 8 * 60 * 60 * 1000;

function formatRemain(ms: number) {
  if (ms <= 0) return "00:00:00";
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
}

const MODE_ITEMS: Array<{
  value: "task" | "caller" | "search";
  label: string;
  icon: ReactNode;
}> = [
  { value: "task", label: "תור", icon: <HeadsetMicRoundedIcon sx={{ fontSize: 18 }} /> },
  { value: "caller", label: "בשיחה", icon: <PersonSearchRoundedIcon sx={{ fontSize: 18 }} /> },
  { value: "search", label: "חיפוש", icon: <SearchRoundedIcon sx={{ fontSize: 18 }} /> },
];

/** Owns the 1s tick so the event card does not re-render every second. */
const ShiftClock: FC<{
  scheduledEndAt?: string | Date | null;
  tourOpen: boolean;
  onOpen: () => void;
  onElapsed: () => void;
}> = ({ scheduledEndAt, tourOpen, onOpen, onElapsed }) => {
  const [now, setNow] = useState(() => Date.now());
  const elapsedFor = useRef<string | null>(null);
  const onElapsedRef = useRef(onElapsed);
  onElapsedRef.current = onElapsed;

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const endMs = scheduledEndAt ? new Date(scheduledEndAt).getTime() : 0;
  const shiftLeft = endMs - now;
  const warn = !tourOpen && shiftLeft <= 15 * 60 * 1000 && shiftLeft > 0;

  useEffect(() => {
    if (tourOpen) return;
    const endKey = scheduledEndAt ? new Date(scheduledEndAt).toISOString() : "";
    if (shiftLeft <= 0 && endKey && elapsedFor.current !== endKey) {
      elapsedFor.current = endKey;
      onElapsedRef.current();
    }
  }, [shiftLeft, tourOpen, scheduledEndAt]);

  return (
    <Stack
      component="button"
      type="button"
      data-tour="cc-shift-clock"
      alignItems="flex-end"
      onClick={onOpen}
      sx={{
        minWidth: 108,
        border: 0,
        bgcolor: "transparent",
        cursor: "pointer",
        font: "inherit",
        color: "inherit",
        textAlign: "end",
        borderRadius: `${CC.radiusXs}px`,
        px: 0.75,
        py: 0.25,
        "&:hover": { bgcolor: "rgba(5,32,76,0.05)" },
      }}
    >
      <Typography
        sx={{
          fontFamily: CC.font,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: warn ? CC.distress : CC.inkMuted,
        }}
      >
        סיום משמרת
      </Typography>
      <Typography
        sx={{
          fontFamily: CC.font,
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "0.04em",
          color: warn ? CC.distress : CC.ink,
        }}
      >
        {tourOpen ? "02:15:00" : formatRemain(shiftLeft)}
      </Typography>
    </Stack>
  );
};

const TaskRemainLabel: FC<{ startedAt: string | Date; minTaskMs: number }> = ({
  startedAt,
  minTaskMs,
}) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const remainMs = Math.max(0, new Date(startedAt).getTime() + minTaskMs - now);
  const label =
    remainMs >= 60_000
      ? `${Math.ceil(remainMs / 60_000)} דק'`
      : `${Math.max(1, Math.ceil(remainMs / 1000))} שנ'`;
  return <>{label}</>;
};

export const CallCenterVolunteerApp: FC<{
  preview?: boolean;
  onClosePreview?: () => void;
}> = ({ preview = false, onClosePreview }) => {
  const {
    workbench,
    tab,
    setTab,
    refresh,
    unreadChat,
    setUnreadChat,
    tourOpen: shiftTourOpen,
    setTourOpen,
    pendingFirstTour,
    setPendingFirstTour,
    setFarewell,
    stationNumber,
    applyWorkbench,
  } = useCallCenterShift();
  const tourOpen = preview || shiftTourOpen;
  const { setAlertText, setSeverity } = useAlert();

  const openOnStation = useCallback(
    async (opportunityId: string) => {
      try {
        const { data } = await linko.callCenter.mutations.openOpportunityOnShift({
          opportunityId,
        });
        applyWorkbench(data);
        setTab("task");
      } catch (error) {
        setSeverity("error");
        setAlertText(
          extractNestHttpErrorMessage(error) || "לא ניתן לפתוח את המשימה בעמדה",
        );
      }
    },
    [applyWorkbench, setAlertText, setSeverity, setTab],
  );

  const searchRowActions = useMemo<SingleRowActionGetter<Opportunity>>(
    () => () => [
      {
        text: "פתיחה בעמדה",
        icon: <PlayArrowRoundedIcon />,
        onClick: async (_, row) => {
          await openOnStation(row.original.id);
          return { refetch: false };
        },
      },
    ],
    [openOnStation],
  );

  const searchSelectionActions = useMemo<ActionButton<Opportunity>[]>(
    () => [
      {
        title: "פתיחה בעמדה",
        icon: <PlayArrowRoundedIcon />,
        onClick: async ({ whereToUpdate }) => {
          const rows = await opportunityRepo.find({
            where: whereToUpdate,
            limit: 2,
            select: { id: true },
          });
          if (rows.length !== 1) {
            setAlertText("בחרו משימה אחת לפתיחה בעמדה");
            setSeverity("warning");
            return { refetch: false, resetSelection: false };
          }
          await openOnStation(rows[0].id);
          return { refetch: false, resetSelection: true };
        },
      },
    ],
    [openOnStation, setAlertText, setSeverity],
  );
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveReason, setLeaveReason] = useState("");
  const [endOpen, setEndOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [distressOpen, setDistressOpen] = useState(false);
  const [tourFocus, setTourFocus] = useState<TourFocus>("tabs");
  const [endEditOpen, setEndEditOpen] = useState(false);
  const [endDraft, setEndDraft] = useState("");

  const tourStepsQuery = useQuery({
    queryKey: ["call-center-tour-steps"],
    enabled: tourOpen,
    queryFn: async () => {
      const { data } = await linko.callCenter.queries.listTourSteps();
      return data.steps;
    },
  });

  const onTourPrepare = useCallback(
    (payload: TourPreparePayload) => {
      setTab(payload.shellTab);
      setTourFocus(payload.focus);
    },
    [setTab],
  );

  const closeTour = useCallback(() => {
    setTourFocus("tabs");
    setTab("task");
    if (preview) {
      onClosePreview?.();
      return;
    }
    setTourOpen(false);
  }, [preview, onClosePreview, setTourOpen, setTab]);

  const remainingGoal = Math.max(
    0,
    (workbench?.volunteer.dailyGoal ?? 0) - (workbench?.shift.matchesCount ?? 0),
  );
  const opportunityId = workbench?.currentTask?.opportunity.id;
  const matches = workbench?.shift.matchesCount ?? 0;
  const goal = workbench?.volunteer.dailyGoal ?? 0;

  useEffect(() => {
    if (!pendingFirstTour) return;
    const id = window.setTimeout(() => {
      setTab("task");
      setTourOpen(true);
      setPendingFirstTour(false);
    }, 80);
    return () => window.clearTimeout(id);
  }, [pendingFirstTour, setTourOpen, setPendingFirstTour, setTab]);

  useEffect(() => {
    if (!workbench?.currentTask || workbench.canAdvance) return;
    const minTaskMs =
      (workbench as { minTaskMs?: number }).minTaskMs ?? CALL_CENTER_MIN_TASK_MS;
    const unlockAt =
      new Date(workbench.currentTask.startedAt).getTime() + minTaskMs;
    const wait = Math.max(0, unlockAt - Date.now()) + 400;
    const id = window.setTimeout(() => void refresh(), wait);
    return () => window.clearTimeout(id);
  }, [workbench?.canAdvance, workbench?.currentTask, refresh]);

  useEffect(() => {
    if (!workbench?.currentTask?.leaveRequestedAt || workbench.canAdvance) return;
    const id = window.setInterval(() => void refresh(), 5000);
    return () => window.clearInterval(id);
  }, [workbench?.currentTask?.leaveRequestedAt, workbench?.canAdvance, refresh]);

  const nextLock = useRef(false);
  const nextTask = async () => {
    if (nextLock.current) return;
    nextLock.current = true;
    const previousTaskId = workbench?.currentTask?.taskId ?? null;
    try {
      const { data } = await linko.callCenter.mutations.requestNextTask();
      applyWorkbench(data);
      setTab("task");
      const nextTaskId = data?.currentTask?.taskId ?? null;
      if (nextTaskId === previousTaskId) {
        setSeverity("info");
        setAlertText("אין משימה פנויה בתור כרגע");
      }
    } catch {
      setSeverity("error");
      setAlertText("לא ניתן לעבור עדיין למשימה הבאה");
    } finally {
      nextLock.current = false;
    }
  };

  const requestLeave = async () => {
    const { data } = await linko.callCenter.mutations.requestEarlyLeave({
      reason: leaveReason,
    });
    applyWorkbench(data);
    setLeaveOpen(false);
    setLeaveReason("");
    setSeverity("info");
    setAlertText("נשלחה בקשה למנהלת");
  };

  const endShift = async () => {
    const { data } = await linko.callCenter.mutations.endShift({ feedback });
    setFarewell(
      `${data.thankYouMessage}${data.nextShiftReminders ? `\n\n${data.nextShiftReminders}` : ""}`,
    );
    setEndOpen(false);
    applyWorkbench(null);
  };

  const canAdvance = !!workbench?.canAdvance;
  const minTaskMs =
    (workbench as { minTaskMs?: number } | null)?.minTaskMs ??
    CALL_CENTER_MIN_TASK_MS;

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: CC.canvas,
        fontFamily: CC.font,
        color: CC.ink,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 1.5, md: 2.5 },
          pt: 1.25,
          pb: 1,
          bgcolor: CC.paper,
          borderBottom: `1px solid ${CC.line}`,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          gap={2}
          sx={{ minHeight: 56, flexWrap: "wrap" }}
        >
          <Stack gap={0.4} data-tour="cc-goal" sx={{ minWidth: 220, flex: 1 }}>
            <CcEyebrow>{CC_BRAND}</CcEyebrow>
            <Stack direction="row" alignItems="baseline" gap={1} flexWrap="wrap">
              <Typography
                sx={{
                  fontFamily: CC.font,
                  fontWeight: 700,
                  fontSize: 16,
                  letterSpacing: "-0.02em",
                }}
              >
                {tourOpen ? "נועה אברהם" : workbench?.volunteer.name}
              </Typography>
              {tourOpen || stationNumber ? (
                <CcChip>עמדה {tourOpen ? 3 : stationNumber}</CcChip>
              ) : null}
              <CcChip tone={!tourOpen && remainingGoal === 0 ? "gold" : "ink"}>
                {tourOpen ? "2/6 מאצ'ים · נשארו 4" : `${matches}/${goal} מאצ'ים`}
                {!tourOpen && remainingGoal > 0 ? ` · נשארו ${remainingGoal}` : ""}
                {!tourOpen && remainingGoal === 0 ? " · היעד הושלם" : ""}
              </CcChip>
            </Stack>
            <CcProgress value={tourOpen ? 2 : matches} max={tourOpen ? 6 : goal} />
          </Stack>

          <ShiftClock
            scheduledEndAt={workbench?.shift.scheduledEndAt}
            tourOpen={tourOpen}
            onElapsed={() => setEndOpen(true)}
            onOpen={() => {
              if (tourOpen) return;
              const end = workbench?.shift.scheduledEndAt
                ? new Date(workbench.shift.scheduledEndAt)
                : new Date(Date.now() + 3 * 60 * 60 * 1000);
              setEndDraft(toDatetimeLocal(end));
              setEndEditOpen(true);
            }}
          />

          <Stack direction="row" gap={1}>
            {preview && (
              <CcButton kind="ghost" onClick={closeTour}>
                סגירת תצוגה
              </CcButton>
            )}
            <CcButton
              data-tour="cc-help"
              kind="ghost"
              onClick={() => {
                if (tourOpen) return;
                setTab("help");
              }}
            >
              <HelpOutlineRoundedIcon sx={{ fontSize: 18 }} />
            </CcButton>
            <CcButton
              data-tour="cc-chat-btn"
              kind="ghost"
              onClick={() => {
                if (tourOpen) return;
                setTab("chat");
                setUnreadChat(0);
              }}
            >
              <Badge
                color="error"
                badgeContent={unreadChat}
                sx={{ "& .MuiBadge-badge": { fontFamily: CC.font } }}
              >
                <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 18 }} />
              </Badge>
            </CcButton>
            <CcButton
              data-tour="cc-distress"
              kind="danger"
              onClick={() => {
                if (tourOpen) return;
                setDistressOpen(true);
              }}
            >
              מצוקה
            </CcButton>
            <CcButton
              data-tour="cc-end-shift"
              kind="ghost"
              onClick={() => {
                if (tourOpen) return;
                setEndOpen(true);
              }}
            >
              יציאה
            </CcButton>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          gap={0.5}
          data-tour="cc-shell-tabs"
          sx={{
            mt: 1.25,
            p: 0.5,
            width: "fit-content",
            maxWidth: "100%",
            bgcolor: "rgba(5,32,76,0.05)",
            borderRadius: 99,
          }}
        >
          {MODE_ITEMS.map((item) => {
            const selected = tab === item.value;
            return (
              <Box
                key={item.value}
                component="button"
                type="button"
                onClick={() => {
                  if (tourOpen) return;
                  setTab(item.value);
                }}
                sx={{
                  appearance: "none",
                  border: 0,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.75,
                  py: 0.85,
                  borderRadius: 99,
                  fontFamily: CC.font,
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: selected ? CC.paper : CC.inkSoft,
                  bgcolor: selected ? CC.ink : "transparent",
                  boxShadow: selected ? CC.shadowSoft : "none",
                  transition: "background-color 160ms ease, color 160ms ease",
                  "&:hover": {
                    bgcolor: selected ? CC.ink : "rgba(5,32,76,0.08)",
                  },
                }}
              >
                {item.icon}
                {item.label}
              </Box>
            );
          })}
        </Stack>
      </Box>

      {tab === "task" && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            p: { xs: 1, md: 1.5 },
            gap: 1.25,
          }}
        >
          {(tourOpen || opportunityId) && (
            <Stack
              direction="row"
              gap={1}
              flexWrap="wrap"
              alignItems="center"
              sx={{ px: 0.5 }}
            >
              <CcButton
                data-tour="cc-next-task"
                kind={canAdvance ? "primary" : "ghost"}
                disabled={!tourOpen && !canAdvance}
                onClick={() => {
                  if (tourOpen) return;
                  void nextTask();
                }}
              >
                {tourOpen
                  ? "למשימה הבאה"
                  : canAdvance
                    ? "אפשר לעבור למשימה הבאה"
                    : "למשימה הבאה"}
              </CcButton>
              <Box data-tour="cc-track">
                <CcTrackChip
                  isGroup={
                    tourOpen
                      ? !!TOUR_OPPORTUNITY.isGroup
                      : !!workbench?.currentTask?.opportunity.isGroup
                  }
                />
              </Box>
              <CcButton
                data-tour="cc-leave-early"
                kind="ghost"
                disabled={
                  !tourOpen &&
                  (canAdvance || !!workbench?.currentTask?.leaveRequestedAt)
                }
                onClick={() => {
                  if (tourOpen) return;
                  setLeaveOpen(true);
                }}
              >
                {!tourOpen && workbench?.currentTask?.leaveRequestedAt
                  ? "הבקשה אצל המנהלת"
                  : tourOpen || !workbench?.currentTask
                    ? "לעבור קודם · 12 דק'"
                    : (
                      <>
                        לעבור קודם ·{" "}
                        <TaskRemainLabel
                          startedAt={workbench.currentTask.startedAt}
                          minTaskMs={minTaskMs}
                        />
                      </>
                    )}
              </CcButton>
              <CcButton
                data-tour="cc-send-chat"
                kind="soft"
                onClick={() => {
                  if (tourOpen || !opportunityId) return;
                  void linko.callCenter.mutations
                    .sendChat({
                      kind: "DETAILS_CARD",
                      body: `משימה: ${workbench?.currentTask?.opportunity.title ?? ""}`,
                      payload: {
                        opportunityId,
                        title: workbench?.currentTask?.opportunity.title,
                        location: workbench?.currentTask?.opportunity.location,
                        isGroup: workbench?.currentTask?.opportunity.isGroup ?? false,
                        trackLabel: workbench?.currentTask?.opportunity.isGroup
                          ? "קבוצה"
                          : "פרטני",
                      },
                    })
                    .then(() => {
                      setSeverity("success");
                      setAlertText("המשימה נשלחה לצ'אט");
                    })
                    .catch(() => {
                      setSeverity("error");
                      setAlertText("לא ניתן לשלוח את המשימה לצ'אט");
                    });
                }}
              >
                שלח משימה לצ'אט
              </CcButton>
            </Stack>
          )}
          {tourOpen ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                height: "100%",
                overflow: "hidden",
                borderRadius: `${CC.radius}px`,
                border: `1px solid ${CC.line}`,
                bgcolor: CC.paperRaised,
                display: "flex",
                flexDirection: "column",
                "& > *": { flex: 1, minHeight: 0 },
              }}
            >
              <CallCenterTourEventMock
                section={
                  tourFocus === "group"
                    ? "participants"
                    : tourFocus === "individual"
                      ? "relevant"
                      : "details"
                }
              />
            </Box>
          ) : !opportunityId ? (
            <Box
              sx={{
                flex: 1,
                bgcolor: CC.paper,
                borderRadius: `${CC.radius}px`,
                border: `1px solid ${CC.line}`,
              }}
            >
              <Stack sx={{ height: "100%" }}>
                <CcEmpty
                  title="אין משימה בתור כרגע"
                  body="נסו שוב מהתור, עברו לחיפוש לפי פרטני או קבוצה, או פנו למנהלת בצ'אט."
                />
                <Stack alignItems="center" sx={{ pb: 3 }}>
                  <CcButton data-tour="cc-next-task" onClick={() => void nextTask()}>
                    משימה מהתור
                  </CcButton>
                </Stack>
              </Stack>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  borderRadius: `${CC.radius}px`,
                  border: `1px solid ${CC.line}`,
                  boxShadow: CC.shadowSoft,
                  bgcolor: CC.paperRaised,
                }}
              >
                {opportunityId ? (
                  <Box
                    data-tour="cc-task-details"
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    <EventCard key={opportunityId} embeddedEventId={opportunityId} />
                  </Box>
                ) : null}
              </Box>
            </>
          )}
        </Box>
      )}
      {tab === "caller" && (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <CallCenterCallerTasks example={tourOpen} />
        </Box>
      )}
      <Box
          data-tour="cc-search-panel"
          sx={{
            flex: tab === "search" ? 1 : undefined,
            minHeight: tab === "search" ? 0 : undefined,
            display: tab === "search" ? "flex" : "none",
            flexDirection: "column",
            overflow: "hidden",
            pointerEvents: tourOpen ? "none" : "auto",
            bgcolor: "#fff",
            borderRadius: `${CC.radius}px`,
            mx: { xs: 1, md: 1.5 },
            mb: { xs: 1, md: 1.5 },
          }}
        >
          <APIProvider
            apiKey={GOOGLE_MAPS_API_KEY}
            libraries={GOOGLE_MAPS_LIBRARIES}
            language={GOOGLE_MAPS_LANGUAGE}
            region={GOOGLE_MAPS_REGION}
          >
            <Suspense
              fallback={
                <Stack flex={1} alignItems="center" justifyContent="center">
                  <CircularProgress />
                </Stack>
              }
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  px: 1.5,
                  py: 1,
                  "& > *": { flex: "1 1 auto", minWidth: 0, minHeight: 0, width: "100%" },
                }}
              >
                <OpportunitiesTable
                  eventBasePath="/events"
                  tourAnchors={tourOpen}
                  previewRows={tourOpen ? TOUR_SEARCH_ROWS : undefined}
                  selectionActions={tourOpen ? undefined : searchSelectionActions}
                  rowActions={tourOpen ? undefined : searchRowActions}
                  onRowActivate={
                    tourOpen ? undefined : (row) => void openOnStation(row.id)
                  }
                  tableId="call-center-search-table"
                  storageScope="call-center-search"
                />
              </Box>
            </Suspense>
          </APIProvider>
        </Box>
      {tab === "help" && (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <CallCenterHelp example={tourOpen} />
        </Box>
      )}
      {tab === "chat" && (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <CallCenterChat example={tourOpen} />
        </Box>
      )}
      <CallCenterTour
        open={tourOpen && !!tourStepsQuery.data?.length}
        steps={tourStepsQuery.data ?? []}
        onClose={closeTour}
        onPrepare={onTourPrepare}
      />
      <Dialog
        open={tourOpen && tourStepsQuery.isSuccess && tourStepsQuery.data.length === 0}
        onClose={closeTour}
        PaperProps={{ sx: ccDialogPaperSx }}
      >
        <DialogTitle sx={{ fontFamily: CC.font, fontWeight: 700 }}>
          אין שלבים פעילים בסיור
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: CC.font, color: CC.inkMuted }}>
            הפעילו לפחות שלב אחד בשולחן הרכזת, ואז פתחו את הסיור שוב.
          </Typography>
        </DialogContent>
        <DialogActions>
          <CcButton onClick={closeTour}>סגירה</CcButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={leaveOpen}
        onClose={() => setLeaveOpen(false)}
        PaperProps={{ sx: ccDialogPaperSx }}
      >
        <DialogTitle sx={{ fontFamily: CC.font, fontWeight: 700, color: CC.ink }}>
          בקשה לעבור משימה מוקדם
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: CC.font, color: CC.inkMuted, mb: 2 }}>
            המנהלת תקבל את הסיבה ותאשר אם אפשר לעבור.
          </Typography>
          <TextField
            fullWidth
            label="סיבה"
            value={leaveReason}
            onChange={(e) => setLeaveReason(e.target.value)}
            sx={ccFieldSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <CcButton kind="ghost" onClick={() => setLeaveOpen(false)}>
            ביטול
          </CcButton>
          <CcButton disabled={!leaveReason.trim()} onClick={() => void requestLeave()}>
            שליחה למנהלת
          </CcButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={distressOpen}
        onClose={() => setDistressOpen(false)}
        PaperProps={{ sx: ccDialogPaperSx }}
      >
        <DialogTitle sx={{ fontFamily: CC.font, fontWeight: 700, color: CC.ink }}>
          לשלוח התראת מצוקה?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: CC.font, color: CC.inkMuted }}>
            המנהלת תקבל התראה מיידית עם העמדה והמשימה הנוכחית.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <CcButton kind="ghost" onClick={() => setDistressOpen(false)}>
            ביטול
          </CcButton>
          <CcButton
            kind="danger"
            onClick={async () => {
              setDistressOpen(false);
              const { data } = await linko.callCenter.mutations.triggerDistress({});
              const alreadyOpen = (data as { alreadyOpen?: boolean } | undefined)
                ?.alreadyOpen;
              setSeverity("warning");
              setAlertText(
                alreadyOpen
                  ? "התראת מצוקה כבר פתוחה אצל המנהלת"
                  : "נשלחה התראת מצוקה למנהלת",
              );
            }}
          >
            שליחה
          </CcButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={endEditOpen}
        onClose={() => setEndEditOpen(false)}
        PaperProps={{ sx: ccDialogPaperSx }}
      >
        <DialogTitle sx={{ fontFamily: CC.font, fontWeight: 700, color: CC.ink }}>
          שעת סיום המשמרת
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: CC.font, color: CC.inkMuted, mb: 2 }}>
            אפשר לקבוע סיום בעתיד, עד 8 שעות מתחילת המשמרת.
          </Typography>
          <TextField
            fullWidth
            type="datetime-local"
            label="סיום"
            value={endDraft}
            onChange={(event) => setEndDraft(event.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={ccFieldSx}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <CcButton kind="ghost" onClick={() => setEndEditOpen(false)}>
            ביטול
          </CcButton>
          <CcButton
            onClick={() => {
              const end = new Date(endDraft);
              const started = workbench?.shift.startedAt
                ? new Date(workbench.shift.startedAt).getTime()
                : Date.now();
              if (Number.isNaN(end.getTime()) || end.getTime() <= Date.now()) {
                setSeverity("warning");
                setAlertText("שעת הסיום צריכה להיות בעתיד");
                return;
              }
              if (end.getTime() > started + SHIFT_MAX_MS) {
                setSeverity("warning");
                setAlertText("אפשר לקבוע סיום עד 8 שעות מתחילת המשמרת");
                return;
              }
              void linko.callCenter.mutations
                .updateShiftEnd({ scheduledEndAt: end.toISOString() })
                .then(({ data }) => applyWorkbench(data))
                .then(() => {
                  setEndEditOpen(false);
                  setSeverity("success");
                  setAlertText("שעת הסיום עודכנה");
                })
                .catch(() => {
                  setSeverity("error");
                  setAlertText("לא ניתן לעדכן את שעת הסיום");
                });
            }}
          >
            שמירה
          </CcButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={endOpen}
        onClose={() => setEndOpen(false)}
        PaperProps={{ sx: ccDialogPaperSx }}
      >
        <DialogTitle sx={{ fontFamily: CC.font, fontWeight: 700, color: CC.ink }}>
          יציאה ממשמרת
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="משוב קצר"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            sx={{ ...ccFieldSx, mt: 1 }}
          />
          <CcButton
            kind="soft"
            sx={{ mt: 1.5 }}
            onClick={async () => {
              try {
                const { data } = await linko.callCenter.mutations.extendShift();
                applyWorkbench(data);
                setEndOpen(false);
              } catch (error) {
                setSeverity("warning");
                setAlertText(
                  extractNestHttpErrorMessage(error) ?? "לא ניתן להאריך את המשמרת",
                );
              }
            }}
          >
            להאריך ב-30 דקות
          </CcButton>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <CcButton kind="ghost" onClick={() => setEndOpen(false)}>
            המשך משמרת
          </CcButton>
          <CcButton onClick={() => void endShift()}>סיום</CcButton>
        </DialogActions>
      </Dialog>

    </Box>
  );
};
